import { startTransition, useEffect, useRef, useState } from 'react';
import { pathName as pathname } from '~/helpers/projectConstant';
import { useHeadroom } from '~/hooks/useHeadroom';
import type { MenuSlug } from '~/types/navigation';

type OpenMenu = MenuSlug | null;

export const useHeader = () => {
  const headroom = useHeadroom({ offset: 64, tolerance: { up: 5, down: 0 } });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const headerRef = useRef<HTMLElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHoveringRef = useRef(false);

  const handleMenuToggle = (menu: MenuSlug) => {
    if (isHoveringRef.current) {
      setOpenMenu(menu);
      return;
    }
    setOpenMenu((prev) => (prev === menu ? null : menu));
  };

  const handleMenuOpen = (menu: MenuSlug) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    isHoveringRef.current = true;
    setOpenMenu(menu);
  };

  const handleMenuLeave = () => {
    isHoveringRef.current = false;
    closeTimerRef.current = setTimeout(() => setOpenMenu(null), 150);
  };

  const handleMenuEnter = () => {
    isHoveringRef.current = true;
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  };

  // Close desktop menu on click outside
  useEffect(() => {
    if (!openMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenu]);

  // Close everything on navigation
  useEffect(() => {
    startTransition(() => {
      setOpenMenu(null);
      setMobileOpen(false);
    });
  }, [pathname]);

  // Close everything on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return {
    headroom,
    headerRef,
    mobileOpen,
    openMenu,
    onMobileToggle: () => setMobileOpen((v) => !v),
    onMenuToggle: handleMenuToggle,
    onMenuOpen: handleMenuOpen,
    onMenuLeave: handleMenuLeave,
    onMenuEnter: handleMenuEnter,
    closeMobile: () => setMobileOpen(false),
  };
};
